import sentinelNetwork from './sentinelNetwork';

export interface AiKpiRepoScore {
  repoSlug: string;
  criticality: string;
  repoScore: number | null;
  repoRag: string | null;
  toolScores: Record<string, number>;
  connectedTools: string[];
  kpiScores: Record<string, number | string | boolean>;
  topFixes: Array<{ kpi: string; tool: string; action: string; location?: string; effort?: string; impact_points?: number; priority?: string }>;
  ragTransition: { previous_score: number; previous_rag: string; direction: string; delta: number } | null;
}

export interface AiKpiScore {
  appId: string;
  analyzedAt: string;
  /** Timestamp of the score computed in the PREVIOUS pipeline run.
   *  Used for the "As of" chip so it always differs from "Last Scan". Null on first run. */
  previousAnalyzedAt?: string | null;
  projectScore: number | null;
  projectRag: string | null;
  projectCriticality: string;
  toolCoverage: { connected: string[]; not_connected: string[]; coverage_pct: number } | null;
  repos: AiKpiRepoScore[];
  aiInsights: { executive_summary: string; insights: unknown[]; error?: string } | null;
}

export const analyzeAiKpi = (payload: {
  appId: string; projectCriticality?: string;
  repos: Array<{ repoSlug: string; branch?: string; criticality?: string; techStack?: string }>;
  toolOverrides?: { gitleaks?: { projects: Array<Record<string, unknown>> }; trivy?: { projects: Array<Record<string, unknown>> } };
  includeAiInsights?: boolean;
}) => sentinelNetwork.post<AiKpiScore>('/ai-kpi/analyze', payload);

/** AI4SWE KPI spec (v2.0) — tool/KPI id → label taxonomy, used to group and label the repo-level KPI breakdown. */
export const KPI_TOOL_ORDER: Array<{ id: string; aliases?: string[]; label: string; kpis: Array<{ id: string; label: string }> }> = [
  {
    id: 'sonarqube', label: 'SonarQube', kpis: [
      { id: 'sonar_gate_passed', label: 'Quality Gate' },
      { id: 'sonar_vulns', label: 'Vulns (Crit+High)' },
      { id: 'sonar_hotspots', label: 'Security Hotspots' },
      { id: 'sonar_coverage', label: 'Test Coverage' },
    ],
  },
  {
    id: 'blackduck', label: 'BlackDuck', kpis: [
      { id: 'bd_critical', label: 'Critical CVEs' },
      { id: 'bd_high', label: 'High CVEs' },
      { id: 'bd_medium', label: 'Medium CVEs' },
      { id: 'bd_critical_high_cves', label: 'Critical+High CVEs' },
      { id: 'bd_licence_high', label: 'High-Risk Licenses' },
    ],
  },
  {
    id: 'trivy', label: 'Trivy (Container)', kpis: [
      { id: 'image_scan_pass', label: 'Image Scan' },
      { id: 'image_critical_cves', label: 'Critical Image CVEs' },
      { id: 'image_high_cves', label: 'High Image CVEs' },
      { id: 'image_age_days', label: 'Image Age (days)' },
    ],
  },
  {
    id: 'ghe', aliases: ['gitleaks'], label: 'Gitleaks / GHE', kpis: [
      { id: 'secrets_detected', label: 'Secrets Detected' },
      { id: 'branch_protected', label: 'Branch Protected' },
      { id: 'pipeline_success', label: 'Pipeline Success %' },
      { id: 'stale_prs', label: 'Stale PRs' },
    ],
  },
  {
    id: 'ai_agent', label: 'Delta AI KPIs', kpis: [
      { id: 'sbom_coverage_pct', label: 'SBOM Coverage' },
      { id: 'transitive_dependency_depth', label: 'Dependency Depth' },
      { id: 'dependency_confusion_risk_score', label: 'Dep. Confusion Risk' },
      { id: 'epss_score', label: 'EPSS Score' },
      { id: 'cisa_kev_match_count', label: 'CISA KEV Matches' },
      { id: 'reachable_vulnerable_functions', label: 'Reachable Vuln Fns' },
      { id: 'unreachable_cve_pct', label: 'Unreachable CVE %' },
      { id: 'mttr_critical', label: 'MTTR (Critical)' },
      { id: 'security_regression_rate', label: 'Security Regression' },
      { id: 'sla_breach_count', label: 'SLA Breaches' },
    ],
  },
];

/** Resolves a tool ID to its KPI_TOOL_ORDER entry, handling aliases (e.g. ghe ↔ gitleaks). */
export function findToolDef(toolId: string) {
  return KPI_TOOL_ORDER.find((t) => t.id === toolId || t.aliases?.includes(toolId));
}

/** Formats a raw KPI value (boolean/number/object) for display in a KPI chip. */
export function formatKpiValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Pass' : 'Fail';
  if (typeof value === 'number') return Number.isInteger(value) ? `${value}` : value.toFixed(2);
  if (value && typeof value === 'object') {
    const netDebt = (value as Record<string, unknown>).net_debt;
    if (netDebt !== undefined) return `net ${netDebt}`;
    return JSON.stringify(value);
  }
  return String(value ?? '—');
}

export const getAiKpiScore = (appId: string) =>
  sentinelNetwork.get<{ success: boolean; score: AiKpiScore | null }>('/ai-kpi-scores', { params: { appId } });

export const getAiKpiScores = (appIds: string[]) =>
  sentinelNetwork.get<{ success: boolean; scores: Record<string, AiKpiScore> }>('/ai-kpi-scores', { params: { appIds: appIds.join(',') } });

export interface AiKpiScoreSummary {
  appId: string;
  projectScore: number | null;
  projectRag: string | null;
  toolCoverage: { connected: string[]; not_connected: string[]; coverage_pct: number } | null;
}

export const getAiKpiScoresSummary = (appIds: string[]) =>
  sentinelNetwork.get<{ success: boolean; scores: Record<string, AiKpiScoreSummary> }>('/ai-kpi-scores', { params: { appIds: appIds.join(','), summary: 'true' } });

export interface ScanProgress {
  status: string;
  passes_completed: number;
  passes_total: number;
  current_pass: string | null;
  elapsed_seconds?: number;
}

export interface AiScanTriggerResponse {
  scanId: string;
  jobId: string;
  status: string;
  alreadyRunning?: boolean;
  message?: string;
}

export interface AiScanStatusResponse {
  status: string;
  scanId?: string;
  triggeredAt?: string;
  completedAt?: string;
  progress: ScanProgress | null;
}

export const triggerAiScan = (appId: string, repos: Array<{ repoSlug: string; url?: string; branch?: string }>) =>
  sentinelNetwork.post<AiScanTriggerResponse>('/ai-scan/trigger', { appId, repos });

export const getAiScanStatus = (appId: string) =>
  sentinelNetwork.get<AiScanStatusResponse>(`/ai-scan/status/${appId}`);

